import { SATError } from "@sadtx/sat-error";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./custom-toast.module.css";

//MARK: Toast Type
export enum CustomToastType {
  // PREREGITRATION_ALARM = "pre-registration-alarm",
  WARNING = "warning",
  SUCCESS = "SUCCESS",
}

// 줄바꿈 문자를 처리하는 헬퍼 함수
function formatTextWithLineBreaks(text: string) {
  return text.split(/\\n|\n/).map((line, index, array) => (
    <span key={index}>
      {line}
      {index < array.length - 1 && <br />}
    </span>
  ));
}

export interface CustomToastProps {
  id: string;
  type: CustomToastType;
  successMessage?: string;
  error: SATError | null;
  createdAt: number;
  // preRegistrationInfo: PreRegistrationPatientInfoEntity | null;
  handleEnrollAction?: () => void;
}
// 전역 토스트 리스트를 함수로 관리
// 테스트를 위해 export (테스트에서 직접 접근 가능)
export let toastErrorList: CustomToastProps[] = [];

//MARK: Toast
export default function CustomToast() {
  
  const [toasts, setToasts] = useState<CustomToastProps[]>([]);
  const [closingIds, setClosingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    // 초기 토스트 리스트 설정
    setToasts([...toastErrorList]);

    // 새로운 토스트가 추가될 때 이벤트 리스너
    const handleToastAdded = () => {
      setToasts([...toastErrorList]);
    };

    // 이벤트 리스너 등록
    window.addEventListener("toastAdded", handleToastAdded);

    return () => {
      window.removeEventListener("toastAdded", handleToastAdded);
    };
  }, []);

  //MARK: Remove Toast
  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    const index = toastErrorList.findIndex((toast) => toast.id === id);
    if (index > -1) {
      toastErrorList.splice(index, 1);
    }
  };

  //MARK: Handle Close
  const handleClose = (id: string) => {
    // 애니메이션을 위해 먼저 closing 상태로 전환한 뒤 제거
    setClosingIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    // CSS transition 시간과 동일하게 맞춤 (0.3s)
    window.setTimeout(() => {
      removeToast(id);
      setClosingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 300);
  };

  //MARK: Render
  return (
    <div
      className={`${styles.toastContainer} ${
        toasts.length === 0 ? styles.toastContainerHidden : ""
      }`}
    >
      {toasts
        .slice()
        .reverse()
        .map((toast, index) => (
          <ToastContent
            key={toast.id}
            toastProps={toast}
            onClose={handleClose}
            index={index}
            isClosing={closingIds.has(toast.id)}
          />
        ))}
    </div>
  );
}

function ToastContent({
  toastProps,
  onClose,
  index,
  isClosing,
}: {
  toastProps: CustomToastProps;
  onClose: (id: string) => void;
  index: number;
  isClosing: boolean;
}) {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    // 일시적 에러 일때만 5초 후 자동으로 제거
    const timer = setTimeout(() => {
      onClose(toastProps.id);
    }, 3000);

    return () => clearTimeout(timer);
  }, [toastProps.id, onClose]);

  // 언어 변경 감지
  useEffect(() => {
    // 언어가 변경되면 컴포넌트가 다시 렌더링되도록 함
  }, [i18n.language]);

  switch (toastProps.type) {
    // case CustomToastType.PREREGITRATION_ALARM:
    //   return (
    //     <div className={`${styles.toastItem} ${isClosing ? styles.toastItemClosing : ""}`}>
    //       <div className={styles.toastContent}>
    //         <img src="/icon/toast/alert.svg" alt="alert" width={16} height={16} />
    //         <div className={styles.toastContentText}>
    //           <span className={styles.patientNameText}>
    //             &quot;{toastProps.preRegistrationInfo?.patient.fullName ?? t("common.patient")}
    //             &quot;
    //           </span>
    //           <span> {formatTextWithLineBreaks(t("common.toast.patientPending"))}</span>
    //         </div>
    //         <img
    //           src="/icon/toast/x.svg"
    //           alt="close"
    //           onClick={() => onClose(toastProps.id)}
    //           className={styles.closeButton}
    //           style={{ width: "16px", height: "16px" }}
    //         />
    //       </div>
    //       <div className={styles.alertToastButtonContainer}>
    //         <button
    //           className={styles.alertToastOkButton}
    //           onClick={() => {
    //             toastProps.handleEnrollAction?.();
    //           }}
    //         >
    //           <span className={styles.alertToastButtonText} style={{ color: "white" }}>
    //             {formatTextWithLineBreaks(t("common.toast.registerButton"))}
    //           </span>
    //         </button>
    //       </div>
    //     </div>
    //   );
    case CustomToastType.WARNING:
      return (
        <div className={`${styles.toastItem} ${isClosing ? styles.toastItemClosing : ""}`}>
          <div className={styles.toastContent}>
            <img
              src={`/icon/toast/${toastProps.type}.svg`}
              alt={toastProps.type}
              width={16}
              height={16}
            />
            <span>
              {formatTextWithLineBreaks(
                toastProps.error?.message ?? t("common.toast.unknownError")
              )}
            </span>
            <img
              src="/icon/toast/x.svg"
              alt="close"
              onClick={() => onClose(toastProps.id)}
              className={styles.closeButton}
              style={{ width: "16px", height: "16px" }}
            />
          </div>
        </div>
      );
    case CustomToastType.SUCCESS:
      return (
        <div className={`${styles.toastItem} ${isClosing ? styles.toastItemClosing : ""}`}>
          <div className={styles.toastContent}>
            <img src="/icon/toast/alert.svg" alt="alert" width={16} height={16} />
            <div className={styles.toastContentText}>
              <span>
                {formatTextWithLineBreaks(
                  toastProps.successMessage ?? t("common.toast.unknownError")
                )}
              </span>
            </div>
            <img
              src="/icon/toast/x.svg"
              alt="close"
              onClick={() => onClose(toastProps.id)}
              className={styles.closeButton}
              style={{ width: "16px", height: "16px" }}
            />
          </div>
        </div>
      );
    default:
      return null;
  }
}

// 일반 경고 토스트 추가 함수
export function addWarningToast(error?: SATError) {
  const newToast: CustomToastProps = {
    error: error ?? null,
    type: CustomToastType.WARNING,
    id: `${Date.now()}-${Math.random()}`,
    createdAt: Date.now(),
    handleEnrollAction: undefined,
  };

  if (error == null) {
    console.error(
      "Warning toast requires an error. Please provide an error in the toast error model."
    );
    return;
  }

  toastErrorList.push(newToast);
  window.dispatchEvent(new CustomEvent("toastAdded"));
}

export function addSuccessToast(message: string) {
  const newToast: CustomToastProps = {
    error: null,
    type: CustomToastType.SUCCESS,
    id: `${Date.now()}-${Math.random()}`,
    successMessage: message,
    createdAt: Date.now(),
    handleEnrollAction: undefined,
  };

  toastErrorList.push(newToast);
  window.dispatchEvent(new CustomEvent("toastAdded"));
}

// // 사전등록 알림 토스트 추가 함수
// export function addPreRegistrationToast(params: {
//   preRegistrationInfo: PreRegistrationPatientInfoEntity;
//   handleEnrollAction: () => void;
// }) {
//   if (!params.preRegistrationInfo) {
//     console.error(
//       "Patient waiting registration error requires patient information and registration action. Please provide PreRegistrationInfo and handleEnrollAction in the toast error model."
//     );
//     return;
//   }

//   const newToast: CustomToastProps = {
//     error: null,
//     type: CustomToastType.PREREGITRATION_ALARM,
//     preRegistrationInfo: params.preRegistrationInfo,
//     id: `${Date.now()}-${Math.random()}`,
//     createdAt: Date.now(),
//     handleEnrollAction: params.handleEnrollAction,
//   };

//   toastErrorList.push(newToast);
//   window.dispatchEvent(new CustomEvent("toastAdded"));
// }
